import React, { useState, useEffect } from 'react';
import { Plus, Edit, User, X, Shield, Users as UsersIcon, Search, Mail, Lock } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'react-toastify';

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ email: '', password: '', fullName: '', phone: '', role: 'staff' });
  const [search, setSearch] = useState('');
  const [smtpAccounts, setSmtpAccounts] = useState([]);

  useEffect(() => {
    loadData();
    loadSmtpAccounts();
  }, []);

  const loadData = async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data.filter(u => u.Role === 'admin' || u.Role === 'staff'));
    } catch (err) {}
    setLoading(false);
  };

  const loadSmtpAccounts = async () => {
    try { const res = await api.get('/settings/smtp-accounts'); setSmtpAccounts(res.data); } catch (err) {}
  };

  const handleSmtpAssign = async (userId, smtpAccountId) => {
    try {
      await api.put(`/settings/users/${userId}/smtp`, { smtpAccountId: smtpAccountId || null });
      toast.success('SMTP ataması güncellendi');
      loadData();
    } catch (err) { toast.error('Atama başarısız'); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/users', form);
      toast.success('Kullanıcı oluşturuldu!');
      setShowForm(false);
      setForm({ email: '', password: '', fullName: '', phone: '', role: 'staff' });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Hata');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        fullName: editingUser.FullName,
        phone: editingUser.Phone,
        role: editingUser.Role,
        isActive: editingUser.IsActive,
        email: editingUser.Email
      };
      if (editingUser.newPassword) {
        updateData.password = editingUser.newPassword;
      }
      await api.put(`/users/${editingUser.Id}`, updateData);
      toast.success('Kullanıcı güncellendi!');
      setEditingUser(null);
      loadData();
    } catch (err) {
      toast.error('Güncelleme başarısız');
    }
  };

  const filteredUsers = users.filter(u => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (u.FullName && u.FullName.toLowerCase().includes(s)) || (u.Email && u.Email.toLowerCase().includes(s));
  });

  const roleLabel = (role) => {
    if (role === 'admin') return { text: 'Yönetici', color: 'bg-red-100 text-red-700' };
    if (role === 'staff') return { text: 'Personel', color: 'bg-blue-100 text-blue-700' };
    return { text: 'Müşteri', color: 'bg-green-100 text-green-700' };
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-slate-800">Kullanıcı Yönetimi</h1>
        <button onClick={() => setShowForm(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">
          <Plus size={18} /> Yeni Personel
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Personel ara (ad, e-posta)..." />
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Kullanıcı</th>
                <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase">E-posta</th>
                <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Rol</th>
                <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase">SMTP Hesabı</th>
                <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Durum</th>
                <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredUsers.map(u => {
                const rl = roleLabel(u.Role);
                return (
                  <tr key={u.Id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                          {u.FullName?.charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800">{u.FullName}</p>
                          {u.Phone && <p className="text-xs text-slate-400">{u.Phone}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-sm text-slate-600">{u.Email}</td>
                    <td className="px-5 py-3"><span className={`text-xs px-2.5 py-1 rounded-full font-medium ${rl.color}`}>{rl.text}</span></td>
                    <td className="px-5 py-3">
                      <select value={u.SmtpAccountId || ''} onChange={e => handleSmtpAssign(u.Id, e.target.value)}
                        className="text-xs px-2 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-purple-400 bg-white min-w-[120px]">
                        <option value="">Varsayılan</option>
                        {smtpAccounts.map(sa => (
                          <option key={sa.Id} value={sa.Id}>{sa.Name} ({sa.FromEmail})</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${u.IsActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {u.IsActive ? 'Aktif' : 'Pasif'}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <button onClick={() => setEditingUser({...u})} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                        <Edit size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-slate-800">Yeni Personel</h3>
              <button onClick={() => setShowForm(false)} className="p-1 text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Ad Soyad *</label>
                <input type="text" value={form.fullName} onChange={e => setForm({...form, fullName: e.target.value})} required
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">E-posta *</label>
                <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Şifre *</label>
                  <input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Telefon</label>
                  <input type="text" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Rol</label>
                <select value={form.role} onChange={e => setForm({...form, role: e.target.value})}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="staff">Personel</option>
                  <option value="admin">Yönetici</option>
                </select>
              </div>
              <button type="submit" className="w-full py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700">Oluştur</button>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-slate-800">Kullanıcı Düzenle</h3>
              <button onClick={() => setEditingUser(null)} className="p-1 text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <form onSubmit={handleUpdate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Ad Soyad</label>
                <input type="text" value={editingUser.FullName} onChange={e => setEditingUser({...editingUser, FullName: e.target.value})}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">E-posta</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-3 text-slate-400" />
                  <input type="email" value={editingUser.Email || ''} onChange={e => setEditingUser({...editingUser, Email: e.target.value})}
                    className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Yeni Şifre <span className="text-xs text-slate-400">(boş bırakılırsa değişmez)</span></label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-3 text-slate-400" />
                  <input type="password" value={editingUser.newPassword || ''} onChange={e => setEditingUser({...editingUser, newPassword: e.target.value})}
                    className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="••••••••" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Telefon</label>
                  <input type="text" value={editingUser.Phone || ''} onChange={e => setEditingUser({...editingUser, Phone: e.target.value})}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Rol</label>
                  <select value={editingUser.Role} onChange={e => setEditingUser({...editingUser, Role: e.target.value})}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="staff">Personel</option>
                    <option value="admin">Yönetici</option>
                  </select>
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={editingUser.IsActive} onChange={e => setEditingUser({...editingUser, IsActive: e.target.checked})}
                  className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                <span className="text-sm text-slate-700">Aktif</span>
              </label>
              <button type="submit" className="w-full py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700">Güncelle</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
